import React, { CSSProperties } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  components,
  default as ReactSelect,
  DropdownIndicatorProps,
  GroupBase,
  MenuPlacement,
  OptionsOrGroups,
  StylesConfig,
} from 'react-select'

import { IconDownArrowInputField } from '../../atoms'
export interface ISelectOption {
  value: any
  label: string | undefined
  icon?: string | undefined
}

export interface ISelectFieldProps {
  options: OptionsOrGroups<any, GroupBase<ISelectOption>>
  placeholder: string
  value?: ISelectOption
  onChange?: (value: any) => void
  onInputChange?: (value: string) => void
  disabled?: boolean
  defaultValue?: any
  menuPlacement?: MenuPlacement
  isClearable?: boolean
  style?: React.CSSProperties
  noOptionsMessage?: string
  disableNoOptionsMessage?: boolean
  menuStyle?: CSSProperties
}

const DropdownIndicator: any = ({ ...props }: DropdownIndicatorProps) => {
  return (
    <components.DropdownIndicator {...props}>
      <IconDownArrowInputField isActive={false} style={{ padding: 0 }} />
    </components.DropdownIndicator>
  )
}

const SelectField = ({
  options,
  placeholder,
  value,
  onChange,
  onInputChange,
  disabled,
  defaultValue,
  menuPlacement,
  isClearable,
  style,
  noOptionsMessage,
  disableNoOptionsMessage,
  menuStyle,
}: ISelectFieldProps) => {
  const customStyles = (): StylesConfig<any, true, GroupBase<unknown>> => ({
    control: (styles, { hasValue }) => ({
      cursor: 'pointer',
      padding: '7px 12px',
      border: '1px solid var(--clr-grey)',
      borderRadius: '6px',
      '&:hover': !hasValue
        ? {
            borderColor: 'var(--clr-action-primary-border-hovered)',
          }
        : {},
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      overflow: 'hidden',
      height: '40px',
      ...style,
    }),
    menu: (styles) => ({
      ...styles,
      padding: 0,
      right: 'auto',
      marginTop: '0px',
      marginBottom: menuPlacement === 'top' ? '0px' : '20px',
      zIndex: 3,
    }),
    option: (styles, { isSelected }: { isSelected: boolean }) => {
      return {
        fontSize: '14px',
        padding: '9px 12px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        color: `${!isSelected ? 'black' : 'var(--clr-primary-400)'}`,
        '&:hover': {
          color: 'var(--clr-primary-600)',
        },
        '&:not(:last-child)': {
          borderBottom: '1px solid var(--border)',
        },
      }
    },
    placeholder: (styles) => ({
      ...styles,
      color: 'black',
      opacity: '0.5',
      fontSize: '14px',
    }),
    singleValue: (styles) => ({
      ...styles,
      fontSize: '14px',
      padding: 0,
      margin: '0',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    valueContainer: (styles) => ({
      ...styles,
      ...{ padding: 0, minWidth: 0, margin: '0.2vh 0', marginRight: '5px' },
    }),
    input: (styles) => ({
      ...styles,
      margin: '0',
      padding: 0,
      fontSize: '14px',
    }),
    dropdownIndicator: () => ({ opacity: '0.5' }),
    menuList: (styles) => ({ ...styles, ...menuStyle }),
  })

  const getNoOptionsMessage = () => {
    if (disableNoOptionsMessage) {
      return null
    } else if (noOptionsMessage) {
      return noOptionsMessage
    }
    return (
      <FormattedMessage
        id='ui.selectField.noResults'
        defaultMessage='Aucun résultat'
      />
    )
  }

  return (
    <ReactSelect
      id='react-select'
      instanceId='react-select'
      placeholder={placeholder}
      components={{ DropdownIndicator }}
      styles={customStyles()}
      options={options}
      onChange={onChange}
      onInputChange={onInputChange}
      isDisabled={disabled}
      value={value}
      defaultValue={defaultValue}
      menuPlacement={menuPlacement}
      isClearable={isClearable}
      noOptionsMessage={getNoOptionsMessage}
    />
  )
}

export default SelectField
